'use client';
import type { CategoryVersionResponse } from '@/types/categories';
import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { categoryTreeKey } from '@/hooks/useCategoryTree';

export function CategoryUpdates() {
  const lang = useLocale();
  const client = useQueryClient();
  const router = useRouter();
  useEffect(() => {
    const controller = new AbortController();
    let version: string | undefined;
    let lastCheck = 0;
    let busy = false;
    const check = async () => {
      if (document.hidden || busy || Date.now() - lastCheck < 30_000) return;
      busy = true;
      lastCheck = Date.now();
      try {
        const response = await fetch(`/api/categories/version?lang=${lang}`, {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return;
        const body: CategoryVersionResponse = await response.json();
        if (version !== body.version) {
          await client.invalidateQueries({ queryKey: categoryTreeKey(lang) });
          if (version !== undefined) router.refresh();
          version = body.version;
        }
      } catch {
        /* Retry on the next interval. */
      } finally {
        busy = false;
      }
    };
    void check();
    const interval = window.setInterval(() => void check(), 30_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      controller.abort();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', check);
    };
  }, [lang, client, router]);
  return null;
}
